import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { FAQ_DATA } from '../data/mockData';

const { FiInfo, FiLayers, FiHelpCircle, FiCheck, FiArrowRight, FiZap, FiTarget } = FiIcons;

export default function Guide() {
  const [activeStep, setActiveStep] = useState(0);

  const STEPS = [
    { title: "Capture", desc: "Connect your calendar or upload audio/video files directly to the Matrix.", icon: FiZap },
    { title: "Process", desc: "Our AI engine transcribes and filters background noise in sub-second time.", icon: FiLayers },
    { title: "Extract", desc: "Key decisions, action items, and insights are automatically labeled.", icon: FiTarget },
    { title: "Review", desc: "Verify extracted intelligence in the Command Console for 100% accuracy.", icon: FiInfo },
    { title: "Distribute", desc: "Sync tasks to Jira, Linear, or Slack to turn talk into execution.", icon: FiCheck },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-20">
      {/* Overview Section */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-cyan-400/20 rounded-lg border border-cyan-400/50">
            <SafeIcon icon={FiInfo} className="text-cyan-400 text-xl" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Overview: Intelligence at Scale</h2>
        </div>
        <div className="glass-panel p-8 rounded-2xl border-l-4 border-l-cyan-400">
          <p className="text-lg text-gray-200 leading-relaxed">
            MeetMatrix is the <span className="text-cyan-400 font-bold">Meeting Intelligence Layer</span> for high-velocity teams. 
            We solve "Meeting Amnesia" by transforming messy conversations into structured data. No more manual note-taking—just pure, 
            actionable decisions delivered to your workflow tools automatically.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="space-y-2">
              <div className="text-cyan-400 font-mono text-xs uppercase tracking-widest">Problem</div>
              <p className="text-sm text-gray-400">Valuable intellectual property is lost in unrecorded or unsummarized calls.</p>
            </div>
            <div className="space-y-2">
              <div className="text-cyan-400 font-mono text-xs uppercase tracking-widest">Solution</div>
              <p className="text-sm text-gray-400">Real-time extraction of decisions and tasks using context-aware LLMs.</p>
            </div>
            <div className="space-y-2">
              <div className="text-cyan-400 font-mono text-xs uppercase tracking-widest">Outcome</div>
              <p className="text-sm text-gray-400">40% reduction in follow-up syncs and 100% accountability on action items.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works / Demo Flow */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-cyan-400/20 rounded-lg border border-cyan-400/50">
            <SafeIcon icon={FiLayers} className="text-cyan-400 text-xl" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Try This Flow</h2>
        </div>
        
        <div className="glass-panel p-8 rounded-2xl">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/3 space-y-4">
              {STEPS.map((step, i) => (
                <button
                  key={i}
                  onMouseEnter={() => setActiveStep(i)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 border ${
                    activeStep === i 
                    ? 'bg-cyan-400/10 border-cyan-400/50 text-cyan-400 shadow-neon' 
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs opacity-50">0{i+1}</span>
                    <span className="font-bold">{step.title}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex-1 bg-black/40 rounded-xl p-8 border border-white/5 flex flex-col justify-center items-center text-center">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="w-16 h-16 bg-cyan-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-400/30">
                  <SafeIcon icon={STEPS[activeStep].icon} className="text-3xl text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white">{STEPS[activeStep].title}</h3>
                <p className="text-gray-400 max-w-sm">{STEPS[activeStep].desc}</p>
                <div className="pt-6 flex justify-center gap-2">
                  {[0,1,2,3,4].map(dot => (
                    <div key={dot} className={`w-2 h-2 rounded-full ${activeStep === dot ? 'bg-cyan-400 shadow-neon' : 'bg-gray-700'}`}></div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-cyan-400/20 rounded-lg border border-cyan-400/50">
            <SafeIcon icon={FiHelpCircle} className="text-cyan-400 text-xl" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">System FAQ</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAQ_DATA.map((item, i) => (
            <div key={i} className="glass-panel p-6 rounded-xl hover:border-cyan-400/30 transition-colors">
              <h4 className="text-white font-bold mb-2 flex gap-2">
                <span className="text-cyan-400">Q:</span> {item.q}
              </h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                <span className="text-gray-600 font-mono">A:</span> {item.a}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}